import React, { useState, useEffect } from 'react';
import { CourseTrainingConfiguration } from '@/types/wordTraining';
import {
  getAllCourseTrainingConfigurations,
  deleteCourseTrainingConfiguration,
  formatFingerCombinationForDisplay
} from '@/services/wordTrainingService';

/**
 * Course Configuration Manager for Admin Dashboard
 * 
 * Provides interface for managing existing course configurations
 * and viewing configuration statistics.
 */

interface CourseConfigurationManagerProps {
  onConfigurationChange?: (config: CourseTrainingConfiguration | null) => void;
}

const CourseConfigurationManager: React.FC<CourseConfigurationManagerProps> = ({
  onConfigurationChange
}) => {
  const [configurations, setConfigurations] = useState<CourseTrainingConfiguration[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadConfigurations();
  }, []);

  const loadConfigurations = async () => {
    setLoading(true);
    setError(null);

    try {
      const configs = await getAllCourseTrainingConfigurations();
      setConfigurations(configs);
    } catch (error) {
      console.error('❌ Error loading configurations:', error);
      setError('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigurationSelect = (config: CourseTrainingConfiguration) => {
    onConfigurationChange?.(config);
  };

  const handleDeleteConfiguration = async (config: CourseTrainingConfiguration) => {
    if (!confirm(`Delete configuration for Level ${config.levelNumber || 1}, Day ${config.dayNumber}, Round ${config.roundNumber}?`)) {
      return;
    }

    try {
      await deleteCourseTrainingConfiguration(config.levelNumber || 1, config.dayNumber, config.roundNumber);
      // Reload configurations
      await loadConfigurations();
    } catch (error) {
      console.error('❌ Error deleting configuration:', error);
      setError('Failed to delete configuration');
    }
  };

  return (
    <div className="course-configuration-manager">
      <div className="manager-header">
        <h3>⚙️ Course Training Configuration Management</h3>
        <p className="manager-description">
          View and manage existing course training configurations for all complexity modes.
        </p>
      </div>

      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <span>Loading configurations...</span>
        </div>
      )}

      {error && (
        <div className="error-state">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && configurations.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h4>No Configurations Found</h4>
          <p>No course training configurations have been created yet.</p>
          <p>Use the Configuration tab to create your first training configuration.</p>
        </div>
      )}

      {configurations.length > 0 && (
        <div className="configurations-list">
          <div className="list-header">
            <div className="header-cell">Level</div>
            <div className="header-cell">Day</div>
            <div className="header-cell">Round</div>
            <div className="header-cell">Fingers</div>
            <div className="header-cell">Hand</div>
            <div className="header-cell">Complexity</div>
            <div className="header-cell">Items</div>
            <div className="header-cell">Actions</div>
          </div>

          {configurations.map((config, index) => {
            const getItemCount = () => {
              if (config.complexity === 'words') {
                if (config.trainingSelection.mode === 'custom' && config.trainingSelection.wordTraining) {
                  if (config.trainingSelection.wordTraining.manual) {
                    const words = config.trainingSelection.wordTraining.manual.selectedWords.length || 0;
                    const phrases = config.trainingSelection.wordTraining.manual.selectedPhrases.length || 0;
                    return words + phrases;
                  } else if (config.trainingSelection.wordTraining.random) {
                    return config.trainingSelection.wordTraining.random.quantity || 0;
                  }
                }
                return 'Default';
              } else {
                if (config.trainingSelection.mode === 'custom' && config.trainingSelection.letterTraining) {
                  return config.trainingSelection.letterTraining.sequenceCount || 'Default';
                }
                return 'Default';
              }
            };

            const getComplexityDisplay = () => {
              if (config.complexity === 'words') {
                return 'Words';
              } else {
                return `Letters 1-${config.complexity}`;
              }
            };

            return (
              <div key={index} className="config-row">
                <div className="cell">Level {config.levelNumber || 1}</div>
                <div className="cell">Day {config.dayNumber}</div>
                <div className="cell">Round {config.roundNumber}</div>
                <div className="cell">{formatFingerCombinationForDisplay(config.fingerCombination)}</div>
                <div className="cell">{config.hand}</div>
                <div className="cell">{getComplexityDisplay()}</div>
                <div className="cell">{getItemCount()}</div>
                <div className="cell">
                  <div className="action-buttons">
                    <button
                      className="action-btn edit-btn"
                      onClick={() => handleConfigurationSelect(config)}
                      title="Edit configuration"
                    >
                      ✏️
                    </button>
                    <button
                      className="action-btn delete-btn"
                      onClick={() => handleDeleteConfiguration(config)}
                      title="Delete configuration"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="manager-actions">
        <button
          className="action-btn refresh-btn"
          onClick={loadConfigurations}
          disabled={loading}
        >
          🔄 Refresh
        </button>
      </div>

      <style>{`
        .course-configuration-manager {
          background: rgba(31, 41, 55, 0.2);
          border-radius: 8px;
          padding: 20px;
          border: 1px solid rgba(75, 85, 99, 0.3);
        }

        .manager-header {
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 1px solid rgba(75, 85, 99, 0.3);
        }

        .manager-header h3 {
          margin: 0 0 8px 0;
          color: #3b82f6;
          font-size: 1.1rem;
        }

        .manager-description {
          margin: 0;
          color: #9ca3af;
          font-size: 0.85rem;
          line-height: 1.4;
        }

        .loading-state {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 40px;
          justify-content: center;
          color: #60a5fa;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(59, 130, 246, 0.3);
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .error-state {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 20px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 6px;
          color: #fca5a5;
        }

        .empty-state {
          text-align: center;
          padding: 40px;
          color: #9ca3af;
        }

        .empty-icon {
          font-size: 3rem;
          margin-bottom: 16px;
        }

        .empty-state h4 {
          margin: 0 0 12px 0;
          color: #e5e7eb;
          font-size: 1.1rem;
        }

        .empty-state p {
          margin: 8px 0;
          font-size: 0.9rem;
          line-height: 1.4;
        }

        .configurations-list {
          background: rgba(17, 24, 39, 0.5);
          border-radius: 6px;
          overflow: hidden;
          margin-bottom: 20px;
        }

        .list-header {
          display: grid;
          grid-template-columns: 80px 80px 80px 1fr 80px 100px 80px 120px;
          gap: 12px;
          padding: 12px 16px;
          background: rgba(59, 130, 246, 0.2);
          border-bottom: 1px solid rgba(75, 85, 99, 0.3);
        }

        .header-cell {
          font-weight: 600;
          color: #e5e7eb;
          font-size: 0.85rem;
        }

        .config-row {
          display: grid;
          grid-template-columns: 80px 80px 80px 1fr 80px 100px 80px 120px;
          gap: 12px;
          padding: 12px 16px;
          border-bottom: 1px solid rgba(75, 85, 99, 0.2);
          transition: background 0.2s ease;
        }

        .config-row:hover {
          background: rgba(75, 85, 99, 0.1);
        }

        .config-row:last-child {
          border-bottom: none;
        }

        .cell {
          display: flex;
          align-items: center;
          color: #d1d5db;
          font-size: 0.85rem;
        }

        .manager-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
        }

        .action-btn {
          padding: 6px 8px;
          border: none;
          border-radius: 4px;
          font-weight: 600;
          font-size: 0.8rem;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
        }

        .edit-btn {
          background: rgba(59, 130, 246, 0.8);
          color: white;
        }

        .delete-btn {
          background: rgba(239, 68, 68, 0.8);
          color: white;
        }

        .delete-btn:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.9);
        }

        .refresh-btn {
          background: rgba(75, 85, 99, 0.8);
          color: white;
        }

        .action-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }

        .action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .list-header,
          .config-row {
            grid-template-columns: 1fr;
            gap: 8px;
          }

          .header-cell,
          .cell {
            justify-content: center;
          }

          .action-buttons {
            justify-content: center;
          }
        }

        @media (max-width: 1024px) {
          .list-header,
          .config-row {
            grid-template-columns: 80px 80px 80px 1fr 80px 120px;
            gap: 8px;
          }

          .cell:nth-child(5), /* Hand column */
          .header-cell:nth-child(5) {
            display: none;
          }

          .cell:nth-child(7), /* Items column */
          .header-cell:nth-child(7) {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default CourseConfigurationManager;
